import { Dropdown, Icon, Label } from '@components';
import { acceptedInputTypes } from '@components/file-upload/constants';
import { businessLicenseDropdownOptions } from '@features/authentication/constants/common';
import { bytesToMB } from '@utils/file';

import theme from '../../../../themes/colors';

interface BusinessLicenseDocumentProps {
  selectedLicenseType: string;
  onLicenseTypeChange: (value: string) => void;
  businessLicenseFile: File | null;
  businessLicenseFileRef: React.RefObject<HTMLInputElement>;
  handleBusinessLicenseFileSelect: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  removeBusinessLicenseFile: () => void;
  existingDocs: { type: string; url: string }[];
  onDownload: () => void;
  removeExistingBusinessLicenseDoc: () => void;
}

const BusinessLicenseDocument = ({
  selectedLicenseType,
  onLicenseTypeChange,
  businessLicenseFile,
  businessLicenseFileRef,
  handleBusinessLicenseFileSelect,
  removeBusinessLicenseFile,
  existingDocs,
  onDownload,
  removeExistingBusinessLicenseDoc
}: BusinessLicenseDocumentProps) => {
  const existingBusinessLicenseDoc = existingDocs?.find(
    (doc) => doc.type === 'BUSINESS_LICENSE'
  );

  const hasFile = !!(businessLicenseFile || existingBusinessLicenseDoc);
  const fileName =
    businessLicenseFile?.name ||
    decodeURIComponent(
      existingBusinessLicenseDoc?.url.split('/').pop()?.split('?')[0] || ''
    ) ||
    '';

  const removeFile = () => {
    if (businessLicenseFile) {
      removeBusinessLicenseFile();
    } else {
      removeExistingBusinessLicenseDoc();
    }
  };

  return (
    <div>
      <Label label="Business license" isRequired className="!mb-1" />
      <div className="flex">
        <Dropdown
          dropdownId="business-license-dropdown"
          currentFilter={selectedLicenseType || null}
          filterOptions={businessLicenseDropdownOptions}
          onSelectFilter={onLicenseTypeChange}
          className="!h-12.5 flex-1 !rounded-r-none"
          buttonClassName="!rounded-r-none !border-cofeeGreyBlue6 !h-12.5 w-full"
          labelStyle="!text-sm !text-cofeeNeutral10"
          usePortal
          listId="business-license-list"
        />
        <input
          type="file"
          ref={businessLicenseFileRef}
          className="hidden"
          onChange={handleBusinessLicenseFileSelect}
          accept={acceptedInputTypes}
          disabled={hasFile}
        />
        <div
          role="presentation"
          onClick={() => !hasFile && businessLicenseFileRef.current?.click()}
          className={`flex h-12.5 shrink-0 items-center gap-2 rounded-r-xl border border-l-0 border-cofeeGreyBlue6 bg-cofeeNeutral1 px-4 text-sm font-medium ${
            hasFile
              ? 'cursor-not-allowed text-cofeeNeutral7 opacity-60'
              : 'cursor-pointer text-primary opacity-100'
          }`}
        >
          {hasFile ? (
            <Icon name="upload-file-neutral" size="medium" />
          ) : (
            <Icon name="upload-file" size="medium" />
          )}
          Upload
        </div>
      </div>
      {hasFile ? (
        <div className="mt-1 flex items-center gap-1 text-sm text-grey">
          <span
            role="presentation"
            onClick={existingBusinessLicenseDoc ? onDownload : undefined}
            className={`max-w-96 !justify-start truncate ${
              existingBusinessLicenseDoc
                ? 'cursor-pointer text-green-600 underline'
                : ''
            }`}
          >
            {fileName}
          </span>
          {businessLicenseFile && <span>|</span>}
          {businessLicenseFile && (
            <span>{bytesToMB(businessLicenseFile.size)}</span>
          )}
          <span>|</span>
          <span
            role="presentation"
            onClick={removeFile}
            className="cursor-pointer text-error"
          >
            Delete
          </span>
        </div>
      ) : (
        <div className="mt-1.5 flex items-center gap-1 text-xs font-normal text-cofeeNeutral7">
          <Icon name="info-circle" stroke={theme.cofeeNeutral8} size="none" />
          Supported formats are PDF, JPG, JPEG, or PNG format
        </div>
      )}
    </div>
  );
};

export default BusinessLicenseDocument;
